import type { AclStats, AclUser, StaffRole } from "@/types";

const ROLE_LABELS: Record<StaffRole, string> = {
  viewer: "Viewer",
  editor: "Editor",
  approver: "Approver",
  admin: "Admin",
  super_admin: "Super Admin",
};

/**
 * Human-readable role label (e.g. "super_admin" -> "Super Admin").
 */
export function formatRoleLabel(role: StaffRole): string {
  return ROLE_LABELS[role];
}

/**
 * Uppercase badge-style role (e.g. "SUPER_ADMIN").
 */
export function formatRole(role: StaffRole): string {
  return role.toUpperCase();
}

/**
 * Derive ACL summary stats from a user list.
 */
export function normalizeAclStats(users: AclUser[]): AclStats {
  return {
    total: users.length,
    superAdmins: users.filter((u) => u.role === "super_admin").length,
    admins: users.filter((u) => u.role === "admin").length,
    editors: users.filter((u) => u.role === "editor").length,
    viewers: users.filter((u) => u.role === "viewer").length,
    active: users.filter((u) => u.status === "active").length,
    inactive: users.filter((u) => u.status === "inactive").length,
  };
}

/**
 * Filter ACL users by name or email search query.
 */
export function filterAclUsers(users: AclUser[], query: string): AclUser[] {
  const q = query.trim().toLowerCase();
  if (!q) return users;

  return users.filter((user) => {
    const name = `${user.firstName} ${user.lastName}`.toLowerCase();
    return name.includes(q) || user.email.toLowerCase().includes(q);
  });
}

/**
 * Paginate a list.
 */
export function paginate<T>(
  items: T[],
  page: number,
  pageSize: number,
): { items: T[]; totalPages: number; start: number; end: number } {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const pageItems = items.slice(startIndex, startIndex + pageSize);

  return {
    items: pageItems,
    totalPages,
    start: items.length === 0 ? 0 : startIndex + 1,
    end: startIndex + pageItems.length,
  };
}
